import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { GraphTileType } from '@/api/rest/resources/types/widget';
import { CanopyCoverHistoricalChart } from '@/components/Charts';
import { ChartHeading } from '@/components/Charts/components/ChartHeading';
import { useChartZoom } from '@/components/Charts/components/ChartZoom';
import { GraphFact } from '@/components/Charts/types';
import { useNCCardContext } from '@/components/NCCard/NCCard';
import { useBenchmarkSelection } from '@/hooks/useBenchmarkSelection';
import { useGetCustomBenchmarks } from '@/hooks/useCustomBenchmarks';
import { useScaledCustomBenchmark } from '@/hooks/useScaledCustomBenchmark';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { usePlot } from '@/pages/shared/hooks/usePlot';

import { NoPlotData } from '../NoPlotData';

export const CanopyCoverGraphTile = ({ graphData }: { graphData: GraphFact[] | null | undefined }) => {
  const { t } = useTranslation();
  const { zoom, setZoom } = useChartZoom();

  const noChartData = !graphData?.length;

  const { analysisType } = useNCCardContext();
  const plot = usePlot().data;
  const plotArea = plot.area;
  const organizationId = useOrganizationId();
  const indicator = GraphTileType.canopy_cover;
  const [selectedBenchmarkId, setSelectedBenchmarkId] = useBenchmarkSelection(indicator);
  const { data: customBenchmarksResp } = useGetCustomBenchmarks(organizationId, indicator);
  const activeCustomBenchmark = customBenchmarksResp?.results?.find((b) => b.id === selectedBenchmarkId);
  const isTotal = analysisType === 'total';
  const displayCustomBenchmark = useScaledCustomBenchmark(activeCustomBenchmark, isTotal, plotArea);

  if (noChartData) {
    return <NoPlotData />;
  }

  const chartData = graphData as GraphFact[];

  return (
    <Stack center data-testid='canopy-cover-graph-tile'>
      <Stack spacing={2.5} className='w-full items-center justify-center px-1 py-6 lg:items-start lg:p-8 lg:pl-1'>
        <ChartHeading
          heading={t('shared.ncaDetail.details.forest.labels.historicalCanopyCoverGraphHeadline')}
          popover={{
            title: t('shared.ncaDetail.details.forest.explainers.historicalCanopyCover.title'),
            body: t('shared.ncaDetail.details.forest.explainers.historicalCanopyCover.body'),
          }}
          className='w-full px-6 sm:px-0'
          zoom={zoom}
          setZoom={setZoom}
          selectedBenchmark={selectedBenchmarkId}
          setSelectedBenchmark={setSelectedBenchmarkId}
          indicator={indicator}
        />
        <CanopyCoverHistoricalChart
          data={chartData}
          zoom={zoom}
          setZoom={setZoom}
          showTooltip={true}
          data-testid='canopy-cover-historic-chart'
          customBenchmark={displayCustomBenchmark}
        />
      </Stack>
    </Stack>
  );
};
